import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import * as Sentry from "@sentry/react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import AddImageBaseCard from "@/Components/Media/User/Images/AddImageBaseCard";
import UserImageBaseCard from "@/Components/Media/User/Images/UserImageBaseCard";
import MediaService from "@/services/user/media";
import {
  UserImageType,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import type { AppUserSession } from "@/schemas/auth/session";
import type { ImageUploadPrompt } from "@/utils/onboarding";
import universalUploadImage from "@/utils/image/universalUploadImage";
import { transformUserImagesToUserLikableImages } from "@/utils/image/images";

type Props = {
  session: AppUserSession;
  prompt: ImageUploadPrompt;
  onImageUploaded: (image: boolean) => void;
  initialImage?: UserLikableImageMedia | null;
};

const OnboardingImageUploadCard = ({
  session,
  prompt,
  onImageUploaded,
  initialImage = null,
}: Props): React.JSX.Element => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadedImage, setUploadedImage] =
    useState<UserLikableImageMedia | null>(initialImage);

  const handleImageUpload = async (): Promise<void> => {
    await universalUploadImage({
      session,
      imageType: UserImageType.PUBLIC_IMAGE,
      onUploadStart: () => setIsUploading(true),
      onUploadSuccess: (userImages) => {
        if (userImages && userImages.length > 0) {
          const userLikableImages =
            transformUserImagesToUserLikableImages(userImages);
          if (userLikableImages.length > 0 && userLikableImages[0]) {
            setUploadedImage(userLikableImages[0]);
            onImageUploaded(true);
          }
        }
      },
      onUploadEnd: () => setIsUploading(false),
    });
  };

  const handleDeleteImage = async (): Promise<void> => {
    if (!uploadedImage || !uploadedImage.image.id || isDeleting) {
      return;
    }

    try {
      const ms = new MediaService(session);
      setIsDeleting(true);
      await ms.deleteImage(uploadedImage.image.id);
      setUploadedImage(null);
      onImageUploaded(false);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert(
        "Oops",
        "Failed to delete the image. Please try again later.",
        [{ text: "OK" }],
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (uploadedImage) {
    return (
      <View style={styles.imageContainer}>
        {/* image */}
        <UserImageBaseCard userLikableImage={uploadedImage} numColumns={3} />
        {/* delete button */}
        <Pressable
          style={styles.deleteButton}
          onPress={handleDeleteImage}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          disabled={isUploading || isDeleting}
        >
          {isDeleting ? (
            <ActivityIndicator size="small" color="gray" />
          ) : (
            <MaterialCommunityIcons
              name="close-circle"
              size={24}
              color="gray"
            />
          )}
        </Pressable>
      </View>
    );
  }

  return (
    <AddImageBaseCard
      handleImageUpload={handleImageUpload}
      numColumns={3}
      iconComponent={
        isUploading ? (
          <MaterialCommunityIcons name="cloud-upload" size={20} color="#999" />
        ) : (
          <Text style={styles.icon}>{prompt.emoji}</Text>
        )
      }
      {...(!isUploading && { title: prompt.title })}
      subtitle={isUploading ? "Uploading..." : prompt.subtitle}
      withActionBarSpacing={false}
    />
  );
};

const styles = StyleSheet.create({
  icon: {
    fontSize: 20,
  },
  imageContainer: {
    position: "relative",
    width: "100%",
  },
  deleteButton: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 10,
    backgroundColor: "rgba(255, 255, 255, 0.75)",
    borderRadius: 18,
    padding: 1,
  },
});

export default OnboardingImageUploadCard;
